import { type FC, useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import { useConfirmAction } from "@/shared/components";
import type { MarketingLanguageType } from "@/types/graphql/partner.generated";
import { LANGUAGE_LABEL_KEYS, MARKETING_LANGUAGES } from "../model/blocks";

const ADD_TAB = "add";

interface VariantTabsProps {
  languages: MarketingLanguageType[];
  active: MarketingLanguageType;
  onSelect: (language: MarketingLanguageType) => void;
  onAdd: (language: MarketingLanguageType) => void;
  onRemove: (language: MarketingLanguageType) => void;
}

/**
 * Вкладки языковых вариантов письма. Последняя вкладка «+» открывает меню
 * языков, которых ещё нет; удалить можно активный вариант, если он не
 * последний (письмо без вариантов бэкенд не примет).
 */
export const VariantTabs: FC<VariantTabsProps> = ({
  languages,
  active,
  onSelect,
  onAdd,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { confirm, dialog } = useConfirmAction();
  const missing = MARKETING_LANGUAGES.filter((l) => !languages.includes(l));

  const onRemoveActive = () =>
    confirm({
      title: t("emailTemplates.variants.removeTitle"),
      text: t("emailTemplates.variants.removeConfirm", {
        language: t(LANGUAGE_LABEL_KEYS[active]),
      }),
      onConfirm: () => onRemove(active),
    });

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Tabs
        value={active}
        onChange={(_event, value: MarketingLanguageType | typeof ADD_TAB) => {
          if (value !== ADD_TAB) onSelect(value);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ flexGrow: 1, minWidth: 0 }}
      >
        {languages.map((language) => (
          <Tab
            key={language}
            value={language}
            label={t(LANGUAGE_LABEL_KEYS[language])}
          />
        ))}
        {missing.length > 0 && (
          <Tab
            value={ADD_TAB}
            icon={<AddIcon fontSize="small" />}
            aria-label={t("emailTemplates.variants.add")}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ minWidth: 48 }}
          />
        )}
      </Tabs>
      {languages.length > 1 && (
        <Tooltip title={t("emailTemplates.variants.remove")}>
          <IconButton
            onClick={onRemoveActive}
            aria-label={t("emailTemplates.variants.remove")}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {missing.map((language) => (
          <MenuItem
            key={language}
            onClick={() => {
              setMenuAnchor(null);
              onAdd(language);
            }}
          >
            {t(LANGUAGE_LABEL_KEYS[language])}
          </MenuItem>
        ))}
      </Menu>
      {dialog}
    </Stack>
  );
};
